package com.milta.feature.optimizationquota.domain.entity

import com.milta.feature.admin.domain.entity.AdminAccount
import kotlinx.serialization.Serializable

/**
 * MIL-97 — one account's RoutaR optimization counter for the current subscription period. Each
 * account has its own (the Enterprise account, and every driver), never a shared pool. [resetAt]
 * is the ISO instant of the next subscription renewal, when [remaining] goes back to [limit].
 */
data class OptimizationQuota(
    val remaining: Int,
    val limit: Int,
    val resetAt: String,
    val accountKind: OptimizationAccountKind,
    val subscriptionKind: OptimizationSubscriptionKind,
    /** An admin replaced [remaining] for the current period (back to [globalLimit] at [resetAt]). */
    val isManuallyAdjusted: Boolean,
    /** The global limit of the account's profile × subscription. Equals [limit] unless an admin
     * replaced the remaining count ([limit] is then consumed + remaining). Shown wherever the text
     * talks about the global / applied limit; the "remaining / limit" badge keeps [limit]. */
    val globalLimit: Int = limit,
) {
    val isExhausted: Boolean get() = remaining <= 0
}

/** Which global limit applies: an Enterprise's driver gets the [Driver] one. */
@Serializable
enum class OptimizationAccountKind { Enterprise, Driver }

/** A driver of an Enterprise is [Trial] while their Enterprise is. */
@Serializable
enum class OptimizationSubscriptionKind { Trial, Paid }

/** Decoded leniently from the history's string (see [fromApi]): a new server value never breaks the list. */
enum class OptimizationAction {
    OptimizeRoute,
    CreateRouteBatch,
    ReoptimizeRouteBatch,
    ReoptimizeOnStopAdded,
    ReoptimizeInPlace,
    /** POST routes/me — a driver's own route, optimized since MIL-97. */
    CreateOwnRoute,
    ;

    companion object {
        fun fromApi(value: String): OptimizationAction? = entries.firstOrNull { it.name == value }
    }
}

/** [Refunded] — RoutaR failed, the optimization was given back and not counted. */
enum class OptimizationOutcome {
    Counted,
    Refunded,
    ;

    companion object {
        /** Anything but "Refunded" counts, as the server only sends these two values. */
        fun fromApi(value: String): OptimizationOutcome = if (value == Refunded.name) Refunded else Counted
    }
}

data class OptimizationHistoryEntry(
    val id: String,
    val occurredAt: String,
    /** `null` for an action this app version doesn't know yet (shown as "—"). */
    val action: OptimizationAction?,
    val routeId: String?,
    val routeBatchId: String?,
    /** "yyyy-MM-dd" — the day of the route / batch concerned. */
    val routeDate: String?,
    val outcome: OptimizationOutcome,
)

/** The 4 global limits, per period (Admin "Limites d'usage"). */
data class OptimizationLimits(
    val enterpriseTrial: Int,
    val enterprisePaid: Int,
    val driverTrial: Int,
    val driverPaid: Int,
)

/** Admin view of an account: an Enterprise's driver is [EnterpriseDriver], not [IndependentDriver]. */
@Serializable
enum class AdminQuotaAccountKind { EnterpriseOwner, EnterpriseDriver, IndependentDriver }

/** Admin "Fiche utilisateur" — [enterpriseName] is null for an independent driver. */
data class AdminAccountOptimizationQuota(
    val account: AdminAccount,
    val accountKind: AdminQuotaAccountKind,
    val enterpriseName: String?,
    val quota: OptimizationQuota,
)
